<template>
  <div>
    <!-- 固定顶部导航栏 -->
    <StickyNav :is-visible="isScrolled" />

    <!-- Header 包含封面和内容 -->
    <AppHeader :title="topicInfo.name" :subtitle="topicInfo.description">
      <!-- 返回按钮 -->
      <div class="container mx-auto px-4 pt-6 max-w-[1800px]">
        <SimpleBackButton />
      </div>

      <!-- 主要内容区域 -->
      <section
        class="container mx-auto px-4 pb-8 sm:pb-12 pt-8 sm:pt-12 max-w-[1800px]"
      >
        <!-- 类别信息卡片 -->
        <div
          class="mb-8 bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300 topic-card"
        >
          <!-- 顶部渐变条 -->
          <div
            class="h-2 topic-gradient"
            :style="{
              background: topicInfo.color.gradient,
            }"
          ></div>

          <!-- 内容区域 -->
          <div class="p-6 sm:p-8 flex items-center gap-6">
            <!-- 图标 -->
            <div
              class="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center text-5xl sm:text-6xl shadow-md topic-icon"
              :style="{
                background: topicInfo.color.gradient,
              }"
            >
              {{ topicInfo.icon }}
            </div>

            <!-- 文字信息 -->
            <div class="flex-1 min-w-0">
              <h2 class="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                {{ topicInfo.name }}
              </h2>
              <p class="text-gray-600 text-sm sm:text-base mb-3">
                {{ topicInfo.description }}
              </p>

              <!-- 统计信息 -->
              <div class="flex items-center gap-4 text-sm">
                <div class="flex items-center gap-1.5 text-gray-500">
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z"
                    />
                  </svg>
                  <span class="font-medium"
                    >{{ filteredPosts.length }} 篇文章</span
                  >
                </div>
                <span class="text-gray-300">•</span>
                <div class="flex items-center gap-1.5 text-gray-500">
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>持续更新</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 文章列表 -->
        <div v-if="filteredPosts.length > 0" class="space-y-6 sm:space-y-8">
          <transition-group
            name="blog-card"
            tag="div"
            class="space-y-6 sm:space-y-8"
          >
            <BlogPostCard
              v-for="(post, index) in paginatedPosts"
              :key="post.id"
              :post="post"
              :index="index"
            />
          </transition-group>
        </div>

        <!-- 空状态 -->
        <div
          v-else
          class="text-center py-16 bg-white rounded-2xl shadow-lg border border-gray-100"
        >
          <div class="text-6xl mb-4">📝</div>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">暂无文章</h3>
          <p class="text-gray-600">该类别下还没有发布文章</p>
        </div>

        <!-- 分页组件 - 始终显示 -->
        <Pagination
          v-model="currentPage"
          :total="filteredPosts.length"
          :page-size="pageSize"
          @change="handlePageChange"
        />
      </section>
    </AppHeader>

    <!-- Footer -->
    <AppFooter />
  </div>
</template>

<script>
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import StickyNav from "@/components/StickyNav.vue";
import AppHeader from "@/components/AppHeader.vue";
import AppFooter from "@/components/AppFooter.vue";
import SimpleBackButton from "@/components/SimpleBackButton.vue";
import BlogPostCard from "@/components/BlogPostCard.vue";
import Pagination from "@/components/Pagination.vue";
import { useScrollNav } from "@/composables/useScrollNav";
import { useBlogPosts } from "@/composables/useBlogPosts";
import { getTopicById } from "@/config/topics";
import { savePageState, getPageState } from "@/utils/navigationState";

export default {
  name: "TopicPage",
  components: {
    StickyNav,
    AppHeader,
    AppFooter,
    SimpleBackButton,
    BlogPostCard,
    Pagination,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { isScrolled } = useScrollNav();
    const { posts } = useBlogPosts();

    const currentPage = ref(1);
    const pageSize = 10; // 类别页面每页显示10篇文章

    // 获取当前类别信息
    const topicInfo = computed(() => {
      const topicId = route.params.topicId;
      const topic = getTopicById(topicId);
      return topic || getTopicById("other");
    });

    // 筛选当前类别的文章
    const filteredPosts = computed(() => {
      const topicId = route.params.topicId;
      return posts.value.filter((post) => post.topicId === topicId);
    });

    // 分页后的文章
    const paginatedPosts = computed(() => {
      const start = (currentPage.value - 1) * pageSize;
      const end = start + pageSize;
      return filteredPosts.value.slice(start, end);
    });

    // 页面切换
    const handlePageChange = (page) => {
      currentPage.value = page;
      // 保存状态
      saveCurrentState();
      // 平滑滚动到顶部
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    };

    // 保存当前状态
    const saveCurrentState = () => {
      const topicId = route.params.topicId;
      const state = {
        page: currentPage.value,
        scrollY: window.scrollY,
      };
      savePageState(`/topic/${topicId}`, state);
    };

    // 恢复状态
    const restoreState = () => {
      const topicId = route.params.topicId;
      const state = getPageState(`/topic/${topicId}`);
      if (state) {
        currentPage.value = state.page || 1;
        // 等待 DOM 更新后恢复滚动位置
        nextTick(() => {
          setTimeout(() => {
            window.scrollTo({
              top: state.scrollY || 0,
              behavior: "auto",
            });
          }, 100);
        });
      }
    };

    // 监听滚动，保存位置
    const handleScroll = () => {
      saveCurrentState();
    };

    // 监听路由变化，重置页码
    watch(
      () => route.params.topicId,
      () => {
        currentPage.value = 1;
      }
    );

    // 检查类别是否存在
    onMounted(() => {
      const topicId = route.params.topicId;
      const topic = getTopicById(topicId);
      if (!topic) {
        router.push("/404");
      } else {
        restoreState();
        window.addEventListener("scroll", handleScroll, { passive: true });
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener("scroll", handleScroll);
    });

    return {
      isScrolled,
      topicInfo,
      filteredPosts,
      paginatedPosts,
      currentPage,
      pageSize,
      handlePageChange,
    };
  },
};
</script>

<style scoped>
/* 类别卡片动画 */
.topic-card {
  position: relative;
  overflow: hidden;
}

.topic-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.3),
    transparent
  );
  transition: left 0.5s ease;
}

.topic-card:hover::before {
  left: 100%;
}

/* 顶部渐变条动画 */
.topic-gradient {
  position: relative;
  overflow: hidden;
}

.topic-gradient::after {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.6),
    transparent
  );
  animation: shimmer 3s infinite;
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 200%;
  }
}

/* 图标悬停效果 */
.topic-icon {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.topic-card:hover .topic-icon {
  transform: scale(1.05) rotate(-3deg);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.2);
}

/* 卡片过渡动画 */
.blog-card-enter-active {
  transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.blog-card-leave-active {
  transition: all 0.4s cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.blog-card-enter-from {
  opacity: 0;
  transform: translateY(30px) scale(0.95);
}

.blog-card-leave-to {
  opacity: 0;
  transform: translateY(-30px) scale(0.95);
}
</style>
